import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useTokens } from 'theme';
import { formatAmount } from './units';
import { useChartSelection } from './chartSelection';
import EChart from './EChart';
import { SIDE, buildTrendOption } from './trendCardOption';
import { trendCard } from './trendCard';

const HEIGHTS = { compact: 128, regular: 220 };

export function Delta({ delta }) {
  const t = useTokens();
  const Icon = delta.value < 0 ? ArrowDownwardIcon : ArrowUpwardIcon;
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1, fontSize: t.chart.type.label, color: t.inkMuted }}>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 1, py: 0.25, borderRadius: '6px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: delta.good ? t.positive : t.negative, bgcolor: delta.good ? t.positiveBg : t.negativeBg }}>
      <Icon aria-hidden sx={{ fontSize: 13 }} />{delta.text}
    </Box>
    {delta.label}
  </Box>;
}

/** The big number, its change and period: one header for cards and full charts. */
export function TrendHeader({ card, compact }) {
  const t = useTokens();
  const { type } = t.chart;
  // Computed numbers are not in the signed rows, so they are not quotable.
  return <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: compact ? type.valueCompact : type.value, fontWeight: type.valueWeight, lineHeight: 1.15, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', color: t.ink, overflowWrap: 'anywhere' }}>{card.value}</Typography>
    {card.delta && <Delta delta={card.delta} />}
    <Typography variant="caption" component="p" sx={{ m: 0, mt: 0.5, mb: 1.5, color: t.inkMuted }}>{card.caption}</Typography>
  </Box>;
}

export default function TrendCard({ chart, columns, rows, meta, compact, height, fallback }) {
  const { t: translate, i18n } = useTranslation();
  const t = useTokens();
  const { fontFamily } = useTheme().typography;
  const card = useMemo(() => trendCard(chart, columns, rows, meta), [chart, columns, rows, meta, i18n.resolvedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps
  const chartHeight = height || HEIGHTS[compact ? 'compact' : 'regular'];
  const view = useRef({ selected: 0, width: 0 }).current;
  const build = useCallback(() => buildTrendOption({ card, view, kind: chart.kind, tone: chart.tone, theme: { ...t, fontFamily }, height: chartHeight, compact }), [card, view, chart.kind, chart.tone, t, fontFamily, chartHeight, compact]);
  // A new card starts from its own highlight; the canvas draws it once sized.
  const selection = useMemo(() => {
    if (card.fallback) return null;
    view.selected = card.selected;
    return {
      view, count: card.buckets.length, initial: card.selected, keyboard: true, patch: build,
      describe: (index) => {
        const bucket = card.buckets[index];
        return `${bucket.name}: ${bucket.value === null ? '—' : formatAmount(bucket.value, card.unit)}`;
      },
    };
  }, [card, view, build]);
  const bind = useChartSelection(selection);

  if (card.fallback) return fallback;
  return <Box sx={{ minWidth: 0 }}>
    <TrendHeader card={card} compact={compact} />
    <Box sx={{ mx: `-${SIDE}px` }}>
      <EChart option={selection.patch} height={chartHeight} bind={bind} aria-label={translate('assistant.trendCardChart', { value1: columns.find((column) => column.key === chart.y[0])?.title || '' })} fallback={fallback} />
    </Box>
    {card.partial && <Typography variant="caption" component="p" sx={{ m: 0, mt: 0.5, color: t.inkMuted, fontSize: t.chart.type.axis }}>{translate('assistant.trendCardPartial')}</Typography>}
  </Box>;
}
